import { TranslateConfigService } from '@mpt-fo/common/core/services/translate-config.service';
import { Component, HostListener, OnInit, Optional, ViewContainerRef } from '@angular/core';
import { Event, NavigationCancel, NavigationEnd, RouteConfigLoadEnd, RouteConfigLoadStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { RouteTrackerService } from '@mpt-fo/common/core/services/route-tracker.service';
import { MobileWebViewService } from '@mpt-fo/common/core/services/mobile-web-view.service';
import { LocalStorageService } from '@mpt-fo/common/core/services/local-storage.service';
import { ScreenManagerService } from '@mpt-fo/common/core/services/screen-manager.service';
import { WEB_CHANNEL_EVENT } from '@mpt-fo/common/shared/constants';
import { logger } from '@mpt-fo/common/telemetry';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { ModalType } from '@mpt-fo/common/kamona/model/kamonal-model';
import { AppConfig } from '@mpt-fo/common/shared/config/app-config';
import { DataLayerService } from '@mpt-fo/common/core/services/data-layer-service.service';
import { LocationHelper } from '@mpt-fo/common/core/helpers/location.helper';
import { StorageKeys } from '@mpt-fo/common/shared/models/storage-keys';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
    title = 'webapp';
    languageUsedCode = 'en';
    languageUsed = 'English';
    readonly LOADING_TEXT = 'Processing...';
    runningModal: NzModalRef;
    readonly ONBOARDING_MODULE_ROUTE = 'onboarding-v2';
    modal: boolean;
    modalType = ModalType.CUSTOM;

    constructor(
        @Optional() public viewRef: ViewContainerRef,
        private translationService: TranslateConfigService,
        private router: Router,
        private routeTrackerService: RouteTrackerService,
        private mobileService: MobileWebViewService,
        private localStorage: LocalStorageService,
        private screenManagerService: ScreenManagerService,
        private dataLayerHelper: DataLayerService,
        private locationHelper: LocationHelper,
        public config: AppConfig,
    ) {
        this.languageUsedCode = this.getLanguage();
        this.languageUsed = this.getLanguageUsed(this.languageUsedCode);
        this.setMobile(window.innerWidth);
        this.setHeight(window.innerHeight);
    }

    ngOnInit(): void {
        this.routeListeners();
        this.handleLocationAccessCheck();

        try {
            if (this.mobileService.isMobileWebViewUser()) {
                this.flutterInAppWebViewPlatformReady();
                this.setWebChannelListener();
            }
            this.trackRoutes();
        } catch (error) {
            logger.error(error);
        }
    }

    async handleLocationAccessCheck() {
        const hasPermission = await this.locationHelper.hasPermission();
        const localPermissionState = this.localStorage.get(StorageKeys.LOCATION_PERMISSION_RESPONSE);

        if (!localPermissionState && !hasPermission) {
            this.router.navigateByUrl('login/location-request-access');
            this.closeInProgressModal();
        }
    }

    routeListeners() {
        this.router.events.subscribe((event) => {
            this.navigationCanceledEventListener(event);
            this.navigationEndedEventListener(event);
            this.setOnboardingModuleListener(event);
        });
    }

    navigationCanceledEventListener(event: Event) {
        if (event instanceof NavigationCancel) {
            this.routeTrackerService.setRoutes(event.url, event.url);
        }
    }

    navigationEndedEventListener(event: Event) {
        if (event instanceof NavigationEnd) {
            this.dataLayerHelper.logPageEvent();
        }
    }

    setOnboardingModuleListener(event: Event) {
        if (!this.config.USE_ONBOARDING_MODULE_LAZY_LOADER) return;

        if (event instanceof RouteConfigLoadStart) {
            this.onRouteConfigLoadStartEvent(event);
        } else if (event instanceof RouteConfigLoadEnd) {
            this.closeInProgressModal();
        }
    }

    onRouteConfigLoadStartEvent(event) {
        if (this.isOnboardingModuleLazyLoading(event)) {
            this.openInProgressModal();
        }
    }

    isOnboardingModuleLazyLoading(event) {
        const route = event?.route;
        const path = route?.path || '';
        return path.includes(this.ONBOARDING_MODULE_ROUTE);
    }

    openInProgressModal() {
        this.modal = true;
    }

    closeInProgressModal() {
        this.modal = false;
    }

    setWebChannelListener() {
        let port;
        window.addEventListener(
            'message',
            (event) => {
                if (event?.data === 'initChannel') {
                    if (event.ports[0] != null) {
                        port = event?.ports[0];
                        // tslint:disable-next-line:no-shadowed-variable
                        port.onmessage = (event: any) => {
                            this.handleWebChannelCommunication(event?.data);
                        };
                    }
                }
            },
            false,
        );
    }

    flutterInAppWebViewPlatformReady() {
        this.mobileService.isFlutterInAppWebViewPlatformReady().then((res) => {
            if (res) {
                return;
            } else {
                window.addEventListener('flutterInAppWebViewPlatformReady', () => {
                    this.mobileService.setFlutterInAppWebViewPlatformReady(true);
                });
            }
        });
    }

    trackRoutes() {
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd), // Use NavigationEnd directly
            )
            .subscribe((event: NavigationEnd) => {
                const savedRoutes = this.routeTrackerService.getRoutes();

                const previousRoute = savedRoutes?.currentRoute === event.urlAfterRedirects ? savedRoutes?.previousRoute : savedRoutes?.currentRoute;

                this.routeTrackerService.setRoutes(previousRoute, event.urlAfterRedirects);
            });
    }

    @HostListener('window:resize', ['$event'])
    onResize() {
        this.setMobile(window.innerWidth);
        this.setHeight(window.innerHeight);
    }

    setMobile(screenSize: number) {
        this.screenManagerService.setMobile(screenSize);
    }

    setHeight(screenHeight: number) {
        this.screenManagerService.setHeight(screenHeight);
    }

    handleWebChannelCommunication(res) {
        const channelResponse = res ? JSON.parse(res) : null;
        if (!channelResponse) {
            return;
        }
        switch (channelResponse?.eventType) {
            case WEB_CHANNEL_EVENT.NOTIFICATION_EVENT:
                this.handleNotificationEvent(channelResponse?.response);
                break;
            case WEB_CHANNEL_EVENT.META_MAP_EVENT:
                this.handleMetaMapEvent(channelResponse?.response);
                break;
        }
    }

    handleNotificationEvent(response) {
        this.mobileService.setNotification(response);
        this.mobileService.setUnReadNotification(true);
        if (this.localStorage.hasAccessToken()) {
            this.router.navigateByUrl(`/notification-center?notification=${JSON.stringify(response)}`);
        }
    }

    handleMetaMapEvent(response) {
        // this.mobileService.callWebChannelObserver(response);
    }

    getLanguage() {
        return this.translationService.getCurrentLanguage();
    }

    changeLanguage(languageCode: string) {
        this.translationService.changeLanguage(languageCode);
        this.languageUsedCode = languageCode;
        this.languageUsed = this.getLanguageUsed(languageCode);
    }

    getLanguageUsed(languageCode: string) {
        switch (languageCode) {
            case 'en':
                return (this.languageUsed = 'English');
            case 'fr':
                return (this.languageUsed = 'French');
            case 'yo':
                return (this.languageUsed = 'Yoruba');
        }
    }
}
